/**
 * Pure logic for the mobile contact packet: phone-number normalisation, the
 * caller match, and parsing a brain-memory row into a typed packet.
 *
 * Kept free of React and of the data client so the matching rules — the part
 * that silently attaches one person's details to another person's thread if
 * it is wrong — can be tested directly.
 */

import {
  MOBILE_CONTACT_PACKET_MEMORY_KEY,
  MOBILE_CONTACT_PACKET_SCOPE,
  type ContactPacketStatus,
  type MobileContactPacket,
  type MobileContactPacketAddress,
  type MobileContactPacketLocation,
  type MobileContactPacketPhone,
  type MobileContactPacketSource,
} from "@/types/contact/mobileContactPacket";

export { MOBILE_CONTACT_PACKET_MEMORY_KEY, MOBILE_CONTACT_PACKET_SCOPE };

/** Shortest and longest digit counts a valid E.164 number can have. */
const E164_MIN_DIGITS = 8;
const E164_MAX_DIGITS = 15;

/**
 * Normalise a phone number to strict E.164 (`+` followed by 8-15 digits).
 *
 * Accepts the international `00` prefix and any spacing/punctuation. Refuses
 * anything without a country code — a bare national number like `021 123 4567`
 * returns null rather than being guessed into a country, because guessing
 * wrong attaches the wrong person's details to a thread.
 */
export function normaliseE164(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  // `00` is the international access prefix in most of the world.
  const withPlus = trimmed.startsWith("00") ? `+${trimmed.slice(2)}` : trimmed;
  if (!withPlus.startsWith("+")) return null;

  const digits = withPlus.slice(1).replace(/\D/g, "");
  if (digits.length < E164_MIN_DIGITS || digits.length > E164_MAX_DIGITS) return null;
  // A country code never starts with 0.
  if (digits.startsWith("0")) return null;

  return `+${digits}`;
}

/**
 * The exact stored strings that should be considered the same caller as
 * `e164`. Used to widen the equality filter without widening the match: every
 * variant normalises back to the same number, and the caller is re-verified
 * with {@link normaliseE164} after the query returns.
 */
export function e164MatchVariants(e164: string): string[] {
  const normalised = normaliseE164(e164);
  if (!normalised) return [];
  return [normalised, normalised.slice(1)];
}

/** True when both numbers are valid E.164 and identical. */
export function isSameCaller(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normaliseE164(a);
  const right = normaliseE164(b);
  return left !== null && left === right;
}

/**
 * The caller's number on a call log row. Inbound calls put the caller in
 * `from_number`, outbound calls put them in `to_number`; an unrecorded
 * direction is treated as inbound, which is the overwhelmingly common case
 * and matches how `saveCallLog` writes the row.
 */
export function callerE164FromCallLog(log: {
  from_number?: string | null;
  to_number?: string | null;
  metadata?: unknown;
}): string | null {
  const direction = asRecord(log?.metadata)?.direction;
  const raw = direction === "outbound" ? log?.to_number : log?.from_number;
  return normaliseE164(raw ?? null);
}

/** A brain-memory row as read for a contact packet. */
export interface ContactPacketMemoryRow {
  caller_e164: string | null;
  metadata: unknown;
  created_at?: string | null;
  updated_at?: string | null;
  expires_at?: string | null;
}

/**
 * True when a brain-memory row has passed its expiry. Rows in this store carry
 * an `expires_at` (conversation memory is written with a 90-day one), so an
 * expired packet must not be shown as current.
 */
export function isMemoryRowExpired(
  row: Pick<ContactPacketMemoryRow, "expires_at"> | null | undefined,
  now: Date = new Date(),
): boolean {
  const expiresAt = text(row?.expires_at);
  if (!expiresAt) return false;
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) return false;
  return parsed <= now.getTime();
}

/**
 * Turn a caller-scoped brain-memory row into a typed packet.
 *
 * Returns null when the row carries no usable field. An all-null packet is
 * never produced: an empty packet must read as empty, not as a populated
 * shape full of blanks.
 */
export function parseMobileContactPacket(
  row: ContactPacketMemoryRow | null | undefined,
): MobileContactPacket | null {
  const callerE164 = normaliseE164(row?.caller_e164);
  if (!callerE164) return null;

  const metadata = asRecord(row?.metadata);
  if (!metadata) return null;

  const phone = parsePhone(metadata.phone);
  const location = parseLocation(metadata.location);
  const address = parseAddress(metadata.address);

  const packet: MobileContactPacket = {
    callerE164,
    name: text(metadata.name) ?? text(metadata.full_name),
    email: text(metadata.email),
    phone,
    location,
    address,
    companyName: text(metadata.company_name),
    jobTitle: text(metadata.job_title),
    source: parseSource(metadata.source),
    collectedAt: text(metadata.collected_at) ?? text(row?.updated_at) ?? text(row?.created_at),
  };

  return isMobileContactPacketEmpty(packet) ? null : packet;
}

/** True when the packet carries no contact detail worth showing. */
export function isMobileContactPacketEmpty(
  packet: MobileContactPacket | null | undefined,
): boolean {
  if (!packet) return true;
  return (
    !packet.name &&
    !packet.email &&
    !packet.companyName &&
    !packet.jobTitle &&
    !formatPacketPhone(packet.phone) &&
    !formatPacketLocation(packet.location) &&
    !formatPacketAddress(packet.address)
  );
}

/** Display string for the packet's phone, or null when there isn't one. */
export function formatPacketPhone(
  phone: MobileContactPacketPhone | null | undefined,
): string | null {
  if (!phone) return null;
  return phone.e164 ?? phone.national ?? null;
}

/** Single-line "City, State, Country" for the packet's location. */
export function formatPacketLocation(
  location: MobileContactPacketLocation | null | undefined,
): string | null {
  if (!location) return null;
  return joinParts([
    location.city,
    location.stateProvince,
    location.countryName ?? location.country,
  ]);
}

/** Single-line postal address for the packet's address. */
export function formatPacketAddress(
  address: MobileContactPacketAddress | null | undefined,
): string | null {
  if (!address) return null;
  return joinParts([
    address.line1,
    address.line2,
    address.city,
    address.stateProvince,
    address.postalCode,
    address.country,
  ]);
}

/**
 * Which of the honest states the contact block should render. Kept pure so
 * the "never show a forever-loading skeleton" rule is testable.
 */
export function resolveContactPacketStatus(input: {
  isLoading: boolean;
  isError: boolean;
  callerE164: string | null | undefined;
  packet: MobileContactPacket | null | undefined;
}): ContactPacketStatus {
  if (input.isLoading) return "loading";
  if (input.isError) return "error";
  if (!normaliseE164(input.callerE164)) return "no-caller";
  if (isMobileContactPacketEmpty(input.packet)) return "empty";
  return "present";
}

// ------------------------------------------------------------------
// internals
// ------------------------------------------------------------------

function parsePhone(value: unknown): MobileContactPacketPhone | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const phone: MobileContactPacketPhone = {
    e164: normaliseE164(text(raw.e164)),
    national: text(raw.national),
    countryCode: text(raw.country_code),
  };
  return phone.e164 || phone.national ? phone : null;
}

function parseLocation(value: unknown): MobileContactPacketLocation | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const location: MobileContactPacketLocation = {
    city: text(raw.city),
    stateProvince: text(raw.state_province),
    country: text(raw.country),
    countryName: text(raw.country_name),
  };
  return formatPacketLocation(location) ? location : null;
}

function parseAddress(value: unknown): MobileContactPacketAddress | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const address: MobileContactPacketAddress = {
    line1: text(raw.line1),
    line2: text(raw.line2),
    city: text(raw.city),
    stateProvince: text(raw.state_province),
    postalCode: text(raw.postal_code),
    country: text(raw.country),
  };
  return formatPacketAddress(address) ? address : null;
}

function parseSource(value: unknown): MobileContactPacketSource {
  return text(value) === "mobile_app" ? "mobile_app" : "unknown";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function joinParts(parts: Array<string | null | undefined>): string | null {
  const kept = parts.map((part) => text(part)).filter((part): part is string => part !== null);
  return kept.length ? kept.join(", ") : null;
}
